import React, { useCallback, useEffect } from "react";
import { connect } from "react-redux";

import Dropdown from "antd/es/dropdown";
import Menu from "antd/es/menu";
import Tabs from "antd/es/tabs";
import { defineMessages, useIntl } from "react-intl";
import { useHistory, useParams } from "react-router-dom";

import {
  EntityType,
  EntityTypesSupportingFieldCustomization,
  entityTypesSupportingFieldCustomization,
} from "@mapmycustomers/shared/types/entity";
import { Button } from "@mapmycustomers/ui";

import Path from "@app/enum/Path";
import SettingPath from "@app/enum/settings/SettingPath";
import { RootState } from "@app/store/rootReducer";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { getEntityTypeAnalyticsName } from "@app/util/ui";

import SettingPage from "../common/SettingPage";

import AssociationsSidebar from "./component/AssociationsSidebar";
import LayoutModal from "./component/LayoutModal";
import LayoutsTable from "./component/LayoutsTable";
import styles from "./FormLayouts.module.scss";
import { isEditingLayoutRolesAssociation } from "./store";
import {
  cancelLayoutRolesAssociation,
  reloadLayoutsAndRoles,
  setActiveEntityType,
  showLayoutRolesAssociation,
} from "./store/actions";

interface Props {
  hideRolesPanel: typeof cancelLayoutRolesAssociation;
  isRolesSidebarVisible: boolean;
  setActiveEntityType: typeof setActiveEntityType;
  showRolesPanel: typeof showLayoutRolesAssociation;
  updateSchema: typeof reloadLayoutsAndRoles;
}

const messages = defineMessages({
  assignLayouts: {
    id: "settings.formLayouts.actions.assignLayouts",
    defaultMessage: "Assign Layouts",
    description: "Title of Assign Layouts button",
  },
  [EntityType.ACTIVITY]: {
    id: "settings.formLayouts.tabs.activities",
    defaultMessage: "Activities",
    description: "Title of Form Layouts - Activities tab",
  },
  [EntityType.COMPANY]: {
    id: "settings.formLayouts.tabs.companies",
    defaultMessage: "Companies",
    description: "Title of Form Layouts - Companies tab",
  },
  [EntityType.DEAL]: {
    id: "settings.formLayouts.tabs.deals",
    defaultMessage: "Deals",
    description: "Title of Form Layouts - Deals tab",
  },
  [EntityType.PERSON]: {
    id: "settings.formLayouts.tabs.people",
    defaultMessage: "People",
    description: "Title of Form Layouts - People tab",
  },
  newLayout: {
    id: "settings.formLayouts.actions.newLayout",
    defaultMessage: "New Layout",
    description: "Title of New Layout button",
  },
  subtitle: {
    id: "settings.formLayouts.subtitle",
    defaultMessage:
      "Create fully-customized forms so your team knows what’s necessary and doesn’t waste time on irrelevant information.",
    description: "Subtitle of settings Form Layouts page",
  },
  title: {
    id: "settings.formLayouts.title",
    defaultMessage: "Layouts",
    description: "Title of settings Form Layouts page",
  },
});

const FormLayouts: React.FC<Props> = ({
  hideRolesPanel,
  isRolesSidebarVisible,
  setActiveEntityType,
  showRolesPanel,
  updateSchema,
}) => {
  const intl = useIntl();
  const history = useHistory();

  const analytics = useExtendedAnalytics(["Settings", "Forms & Fields", "Layouts"], useAnalytics());
  const newLayoutAnalytics = useExtendedAnalytics(["New Layout"], analytics);

  const { id, tab = EntityType.COMPANY } = useParams<{
    id: string;
    tab: EntityTypesSupportingFieldCustomization;
  }>();
  const entityType = tab as EntityTypesSupportingFieldCustomization;

  useEffect(() => {
    setActiveEntityType(entityType);
    updateSchema();
    hideRolesPanel();
  }, [entityType, hideRolesPanel, setActiveEntityType, updateSchema]);

  const handleAssignRoles = useCallback(() => showRolesPanel(), [showRolesPanel]);

  const handleNewLayout = useCallback(
    ({ key }) => {
      newLayoutAnalytics.clicked([getEntityTypeAnalyticsName(key, false, true)]);

      hideRolesPanel();

      history.push(`${Path.SETTINGS}/${SettingPath.FORMS_CREATE}/${key}/create`);
    },
    [newLayoutAnalytics, hideRolesPanel, history]
  );

  const handleTabClick = useCallback(
    (key) => {
      analytics.clicked([getEntityTypeAnalyticsName(key, false, true)]);
      history.push(`${Path.SETTINGS}/${SettingPath.FORMS_CREATE}/${key}`);
    },
    [analytics, history]
  );

  return (
    <SettingPage
      actions={
        <div className={styles.actions}>
          <Button onClick={handleAssignRoles}>{intl.formatMessage(messages.assignLayouts)}</Button>

          <Dropdown
            overlay={
              <Menu onClick={handleNewLayout}>
                {entityTypesSupportingFieldCustomization.map((entityType) => (
                  <Menu.Item key={entityType}>{intl.formatMessage(messages[entityType])}</Menu.Item>
                ))}
              </Menu>
            }
            trigger={["click"]}
          >
            <Button type="primary">{intl.formatMessage(messages.newLayout)}</Button>
          </Dropdown>
        </div>
      }
      pageContentClassName={styles.pageContent}
      subTitle={intl.formatMessage(messages.subtitle)}
      title={intl.formatMessage(messages.title)}
    >
      <div className={styles.container}>
        <div className={styles.tabs}>
          <Tabs activeKey={entityType} onTabClick={handleTabClick}>
            {entityTypesSupportingFieldCustomization.map((entityType) => (
              <Tabs.TabPane key={entityType} tab={intl.formatMessage(messages[entityType])} />
            ))}
          </Tabs>
        </div>

        <LayoutsTable analyticsIssuer={analytics} />

        {id && (
          <AnalyticsContext.Provider value={newLayoutAnalytics}>
            <LayoutModal entityType={entityType} id={id === "create" ? id : Number(id)} />
          </AnalyticsContext.Provider>
        )}

        {isRolesSidebarVisible && <AssociationsSidebar />}
      </div>
    </SettingPage>
  );
};

const mapStateToProps = (state: RootState) => ({
  isRolesSidebarVisible: isEditingLayoutRolesAssociation(state),
});

const mapDispatchToProps = {
  hideRolesPanel: cancelLayoutRolesAssociation,
  setActiveEntityType,
  showRolesPanel: showLayoutRolesAssociation,
  updateSchema: reloadLayoutsAndRoles,
};

export default connect(mapStateToProps, mapDispatchToProps)(FormLayouts);
